import { z } from 'zod';

const tiptapMarkSchema = z
  .object({
    type: z.string().describe('Mark type (e.g. bold, italic, link).'),
    attrs: z.record(z.string(), z.unknown()).optional(),
  })
  .describe('Inline formatting applied to text.');

const tiptapTextNodeSchema = z.object({
  type: z.literal('text'),
  text: z.string().describe('Text content.'),
  marks: z.array(tiptapMarkSchema).optional(),
});

const tiptapContentNodeSchema: z.ZodType = z.lazy(() =>
  z.union([
    tiptapTextNodeSchema,
    z
      .object({
        type: z
          .string()
          .describe(
            'Block or inline node type (paragraph, heading, mention, section, etc.).'
          ),
        attrs: z
          .record(z.string(), z.unknown())
          .optional()
          .describe(
            'Node-specific attributes (e.g. mention id, heading level).'
          ),
        content: z.array(tiptapContentNodeSchema).optional(),
        marks: z.array(tiptapMarkSchema).optional(),
      })
      .catchall(z.unknown()),
  ])
);

/**
 * TipTap/ProseMirror document root used for agent and document `editorState` fields.
 * Permissive at runtime via `.catchall()` so Cotera-specific node types are accepted.
 */
export const editorStateSchema = z
  .object({
    type: z.literal('doc').describe('Root node type; always "doc".'),
    content: z
      .array(tiptapContentNodeSchema)
      .optional()
      .describe('Top-level block nodes in document order.'),
  })
  .catchall(z.unknown())
  .describe(
    'TipTap JSON document (Cotera DG DocumentEditor format). Defines agent instructions or document body, including @mentions of tools and other resources.'
  );

/** LLM model identifier passed to agent create/update and chat endpoints. */
export const llmModelIdSchema = z
  .string()
  .describe(
    'LLM model identifier (Cotera model slug, e.g. "azure-gpt-5.5"). Must be a model enabled for the organization.'
  );

/** Root shape returned by {@link sanitizeEditorState}; always satisfies {@link editorStateSchema}. */
export type SanitizedEditorState = { type: 'doc'; content: unknown[] } & Record<
  string,
  unknown
>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray(value) === false
  );
}

function sanitizeMark(mark: unknown): Record<string, unknown> | null {
  if (isPlainObject(mark) === false || typeof mark['type'] !== 'string') {
    return null;
  }
  const out: Record<string, unknown> = { ...mark };
  if ('attrs' in out && isPlainObject(out['attrs']) === false) {
    delete out['attrs'];
  }
  return out;
}

// ---------------------------------------------------------------------------
// Table structure normalization
//
// ProseMirror's table schema is strict: `table` holds `tableRow+`, a `tableRow`
// holds `(tableCell | tableHeader)+`, and every cell holds `block+`. An LLM
// writing table JSON through the Coco tools routinely violates this — a cell
// whose only child is a bare text node, an empty cell, cells placed directly
// under the table with no row, or ragged rows. The shape schema accepts all of
// these, they get persisted, and then the client editor throws
// `Called contentMatchAt on a node with invalid content` on open (crashing the
// whole app) while the markdown read-back degrades. We repair the structure
// here so an invalid table can never reach the column.
// ---------------------------------------------------------------------------

/**
 * Inline node types that cannot be a direct child of a table cell (cell content
 * is `block+`), so a run of them is wrapped in a paragraph. This is the complete
 * set of inline nodes in the DG document schema: `text` and `hardBreak` from the
 * base kit, `mention` (group `inline`), and `emoji` (inline atom); every other
 * node type is a block.
 *
 * This is an allowlist of inline types on purpose — only types we are certain are
 * inline get wrapped. An unknown type is left as-is (assumed block): wrapping an
 * unknown-but-actually-block node in a paragraph would produce `paragraph > block`,
 * which is itself invalid — worse than leaving it. If a new inline node type is
 * added to the schema, add it here (and to the tests below).
 */
const INLINE_NODE_TYPES = new Set(['text', 'hardBreak', 'mention', 'emoji']);

function nodeTypeOf(node: Record<string, unknown>): string {
  return node['type'] as string;
}

function childNodes(node: Record<string, unknown>): Record<string, unknown>[] {
  return Array.isArray(node['content'])
    ? node['content'].filter(
        (n): n is Record<string, unknown> =>
          isPlainObject(n) && typeof n['type'] === 'string'
      )
    : [];
}

function emptyParagraph(): Record<string, unknown> {
  return { type: 'paragraph' };
}

function emptyCell(): Record<string, unknown> {
  return { type: 'tableCell', content: [emptyParagraph()] };
}

// A span (colspan/rowspan) is clamped to [1, MAX_TABLE_SPAN] and the padded grid
// to MAX_TABLE_COLUMNS, so a pathological cell (e.g. `colspan: 1000000`) can
// neither be persisted verbatim — where it would blow up the client's table map
// — nor amplified here into a huge grid of fabricated empty cells (an OOM/DoS
// vector in the padding loop). Both bounds are far above any real table, and are
// exported so callers/tests can reference the exact limits.
export const MAX_TABLE_SPAN = 100;
export const MAX_TABLE_COLUMNS = 100;

/** Coerce a span attr to an int in [1, MAX_TABLE_SPAN]; anything invalid -> 1. */
function clampSpan(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 1) {
    return Math.min(Math.floor(value), MAX_TABLE_SPAN);
  }
  return 1;
}

/**
 * Clamp a cell's `colspan`/`rowspan` attrs in place (only the keys present, so a
 * cell without attrs stays untouched). Returns the same node when nothing
 * changed, so well-formed cells pass through by identity.
 */
function sanitizeCellAttrs(
  node: Record<string, unknown>
): Record<string, unknown> {
  const attrs = node['attrs'];
  if (isPlainObject(attrs) === false) {
    return node;
  }
  const next: Record<string, unknown> = { ...attrs };
  let changed = false;
  for (const key of ['colspan', 'rowspan'] as const) {
    if (key in next) {
      const clamped = clampSpan(next[key]);
      if (clamped !== next[key]) {
        changed = true;
      }
      next[key] = clamped;
    }
  }
  return changed ? { ...node, attrs: next } : node;
}

/** `colspan` a cell occupies (defaults to 1); attrs are already clamped. */
function cellColspan(cell: Record<string, unknown>): number {
  const attrs = cell['attrs'];
  if (isPlainObject(attrs) && typeof attrs['colspan'] === 'number') {
    return clampSpan(attrs['colspan']);
  }
  return 1;
}

/** `rowspan` a cell occupies (defaults to 1); attrs are already clamped. */
function cellRowspan(cell: Record<string, unknown>): number {
  const attrs = cell['attrs'];
  if (isPlainObject(attrs) && typeof attrs['rowspan'] === 'number') {
    return clampSpan(attrs['rowspan']);
  }
  return 1;
}

function rowWidth(row: Record<string, unknown>): number {
  return childNodes(row).reduce((sum, cell) => sum + cellColspan(cell), 0);
}

/** Cell content must be `block+`: wrap inline runs in paragraphs; never empty. */
function normalizeCellContent(
  children: Record<string, unknown>[]
): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  let inlineRun: Record<string, unknown>[] = [];
  const flushInline = (): void => {
    if (inlineRun.length > 0) {
      out.push({ type: 'paragraph', content: inlineRun });
      inlineRun = [];
    }
  };
  for (const child of children) {
    if (INLINE_NODE_TYPES.has(nodeTypeOf(child))) {
      inlineRun.push(child);
    } else {
      flushInline();
      out.push(child);
    }
  }
  flushInline();
  return out.length > 0 ? out : [emptyParagraph()];
}

/** Row content must be `(tableCell|tableHeader)+`: wrap stray content in a cell. */
function normalizeRowContent(
  children: Record<string, unknown>[]
): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  let strayRun: Record<string, unknown>[] = [];
  const flushStray = (): void => {
    if (strayRun.length > 0) {
      out.push({ type: 'tableCell', content: normalizeCellContent(strayRun) });
      strayRun = [];
    }
  };
  for (const child of children) {
    const type = nodeTypeOf(child);
    if (type === 'tableCell' || type === 'tableHeader') {
      flushStray();
      out.push(child);
    } else {
      strayRun.push(child);
    }
  }
  flushStray();
  return out;
}

/**
 * Table content must be `tableRow+`. Wraps orphaned cells (and any other stray
 * content) into rows and drops empty rows, then — for simple grids — pads short
 * rows with empty cells so every row has the same column count.
 *
 * Padding is skipped when the table uses `rowspan` (a per-row column count is
 * wrong when a cell spans down into the row, so fabricating cells would corrupt
 * it) or when the grid is implausibly wide (`> MAX_TABLE_COLUMNS`). In both
 * cases the structural fixes still apply and the client's table-fixing pass
 * reconciles the geometry — we just don't invent cells here.
 *
 * Returns `[]` when no row has any cell, which drops the (contentless) table
 * entirely — the only case where a table is removed, and it carries no text.
 */
function normalizeTableContent(
  children: Record<string, unknown>[]
): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  let orphanCells: Record<string, unknown>[] = [];
  const flushOrphans = (): void => {
    if (orphanCells.length > 0) {
      rows.push({ type: 'tableRow', content: orphanCells });
      orphanCells = [];
    }
  };
  for (const child of children) {
    const type = nodeTypeOf(child);
    if (type === 'tableRow') {
      flushOrphans();
      rows.push(child);
    } else if (type === 'tableCell' || type === 'tableHeader') {
      orphanCells.push(child);
    } else {
      // Stray block inside the table (e.g. a paragraph): fold it into a cell.
      orphanCells.push({
        type: 'tableCell',
        content: normalizeCellContent([child]),
      });
    }
  }
  flushOrphans();

  const nonEmptyRows = rows.filter((row) => childNodes(row).length > 0);
  if (nonEmptyRows.length === 0) {
    return [];
  }

  const usesRowspan = nonEmptyRows.some((row) =>
    childNodes(row).some((cell) => cellRowspan(cell) > 1)
  );
  const maxWidth = Math.max(...nonEmptyRows.map(rowWidth));
  if (usesRowspan || maxWidth > MAX_TABLE_COLUMNS) {
    return nonEmptyRows;
  }

  for (const row of nonEmptyRows) {
    const cells = childNodes(row);
    for (let w = rowWidth(row); w < maxWidth; w += 1) {
      cells.push(emptyCell());
    }
    row['content'] = cells;
  }
  return nonEmptyRows;
}

/**
 * Enforce ProseMirror's structural table rules on an already shape-sanitized
 * node. Non-table nodes are returned unchanged; a table left with no valid rows
 * is dropped (returns null).
 */
function normalizeTableStructure(
  node: Record<string, unknown>
): Record<string, unknown> | null {
  switch (nodeTypeOf(node)) {
    case 'tableCell':
    case 'tableHeader': {
      const clamped = sanitizeCellAttrs(node);
      return { ...clamped, content: normalizeCellContent(childNodes(clamped)) };
    }
    case 'tableRow':
      return { ...node, content: normalizeRowContent(childNodes(node)) };
    case 'table': {
      const content = normalizeTableContent(childNodes(node));
      return content.length > 0 ? { ...node, content } : null;
    }
    default:
      return node;
  }
}

/**
 * Coerce a single content entry into a node that satisfies
 * `tiptapContentNodeSchema`, or drop it (returns null) when it cannot be.
 *
 * LLMs occasionally emit a content entry as a JSON string (recovered here via
 * `JSON.parse`) or as a primitive / `type`-less object, and set `content`,
 * `marks`, or `attrs` to non-array / non-object values — all of which the
 * strict schema rejects. We repair recursively so the result always parses.
 */
function sanitizeNode(node: unknown): Record<string, unknown> | null {
  let value = node;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (isPlainObject(value) === false || typeof value['type'] !== 'string') {
    return null;
  }
  const out: Record<string, unknown> = { ...value };
  if ('attrs' in out && isPlainObject(out['attrs']) === false) {
    delete out['attrs'];
  }
  if ('content' in out) {
    if (Array.isArray(out['content'])) {
      out['content'] = out['content']
        .map(sanitizeNode)
        .filter((n): n is Record<string, unknown> => n !== null);
    } else {
      delete out['content'];
    }
  }
  if ('marks' in out) {
    if (Array.isArray(out['marks'])) {
      out['marks'] = out['marks']
        .map(sanitizeMark)
        .filter((m): m is Record<string, unknown> => m !== null);
    } else {
      delete out['marks'];
    }
  }
  // Runs after the recursive content pass above, so by the time a `table` is
  // normalized its rows (and their cells) are already structurally sound.
  return normalizeTableStructure(out);
}

/**
 * Repair an arbitrary value into a TipTap document that is guaranteed to pass
 * {@link editorStateSchema} (`sanitizeEditorState(x)` always parses).
 *
 * This closes the validation asymmetry between the two writers of the
 * `editorState` column: Coco tools persist via the resource strategy with only
 * a shallow shape check, while the HTTP update route validates against
 * `editorStateSchema`. Running every persisted value through this normalizer at
 * the strategy `save()` chokepoint means the lax write path can no longer store
 * a document the strict read/update path would reject — and legacy rows
 * self-heal the next time they are saved.
 *
 * Valid documents (including Cotera custom nodes like `mention` / `section` /
 * `response-shape`) pass through unchanged; only malformed nodes are repaired
 * or dropped.
 */
export function sanitizeEditorState(raw: unknown): SanitizedEditorState {
  const base = isPlainObject(raw) ? raw : {};
  const content = Array.isArray(base['content'])
    ? base['content']
        .map(sanitizeNode)
        .filter((n): n is Record<string, unknown> => n !== null)
    : [];
  return { ...base, type: 'doc', content };
}
